/**
 * Queue processor with exponential backoff retry logic.
 *
 * Key behaviors:
 * - Single-processor pattern (prevents concurrent processing)
 * - Exponential backoff: 30s, 60s, 120s, 240s, 300s (max)
 * - Error classification: permanent vs transient failures
 * - Continues processing even if one chunk fails
 */
import { logTimestamp } from "./index";
import type {
  ProcessChunkFn,
  TranscriptionOutcome,
  TranscriptionQueueItem,
} from "./types";

const LOG_PREFIX = () => `[${logTimestamp()}][TranscriptionQueue:Processor]`;

/** Base delay for retry backoff (3 seconds) */
const BASE_DELAY_MS = 3_000; // 3 seconds

/** Maximum delay between retries (5 minutes) */
const MAX_DELAY_MS = 300_000;

/** Maximum number of retry attempts */
export const MAX_RETRIES = 5;

/**
 * Calculates the next retry time using exponential backoff with jitter.
 *
 * Formula: min(30000 * 2^retryCount, 300000) + jitter
 * Jitter: +/- 10% to prevent thundering herd
 */
export function calculateNextRetryTime(retryCount: number): number {
  const delay = Math.min(BASE_DELAY_MS * Math.pow(2, retryCount), MAX_DELAY_MS);
  // Add jitter: +/- 10%
  const jitter = delay * 0.1 * (Math.random() * 2 - 1);
  const nextRetryAt = Date.now() + delay + jitter;
  console.log(
    `${LOG_PREFIX()} Calculated next retry: attempt=${retryCount + 1}, delay=${Math.round((delay + jitter) / 1000)}s`,
  );
  return nextRetryAt;
}

/**
 * Classifies an HTTP error to determine if it's permanent or transient.
 *
 * Permanent errors (should NOT retry):
 * - 400: Bad request (corrupt audio, invalid params, note already completed)
 * - 404: Resource not found (note doesn't exist)
 *
 * Transient errors (SHOULD retry):
 * - 401/403: Auth errors (token may need refresh)
 * - 5xx: Server errors
 * - Network errors
 * - Timeouts
 */
export function classifyError(error: unknown): {
  permanent: boolean;
  message: string;
} {
  // Handle HTTP errors with status codes
  if (error && typeof error === "object") {
    // Check for response status (ky error shape)
    const errorObj = error as {
      response?: { status?: number };
      message?: string;
    };
    const status = errorObj.response?.status;

    if (status === 400) {
      console.log(
        `${LOG_PREFIX()} Error classified as PERMANENT (400): ${errorObj.message}`,
      );
      return {
        permanent: true,
        message: errorObj.message || "Bad request - audio may be corrupt",
      };
    }

    if (status === 404) {
      // 404 means the note doesn't exist - permanent failure
      console.log(
        `${LOG_PREFIX()} Error classified as PERMANENT (404): ${errorObj.message}`,
      );
      return {
        permanent: true,
        message: errorObj.message || "Note not found",
      };
    }

    if (status === 401 || status === 403) {
      console.log(
        `${LOG_PREFIX()} Error classified as TRANSIENT (${status} auth): ${errorObj.message}`,
      );
      return {
        permanent: false,
        message: errorObj.message || "Authentication error",
      };
    }

    // 5xx errors are transient
    if (status && status >= 500) {
      console.log(
        `${LOG_PREFIX()} Error classified as TRANSIENT (${status} server): ${errorObj.message}`,
      );
      return {
        permanent: false,
        message: errorObj.message || `Server error (${status})`,
      };
    }
  }

  // Network errors, timeouts, etc. are transient
  const message =
    error instanceof Error ? error.message : "Unknown error occurred";

  console.log(
    `${LOG_PREFIX()} Error classified as TRANSIENT (network/unknown): ${message}`,
  );
  return {
    permanent: false,
    message,
  };
}

/**
 * Determines if a queue item is ready to be processed.
 * An item is ready if:
 * 1. It's pending (never tried), OR
 * 2. It's failed but ready for retry (nextRetryAt <= now)
 */
export function isReadyForProcessing(item: TranscriptionQueueItem): boolean {
  if (item.status === "pending") {
    return true;
  }

  if (item.status === "failed" && item.nextRetryAt) {
    return item.nextRetryAt <= Date.now();
  }

  return false;
}

/**
 * Sorts items by priority for processing.
 * Priority order:
 * 1. Pending items first
 * 2. Within same note, sort by creation time (oldest first)
 * 3. Failed items ready for retry (by nextRetryAt)
 */
export function sortByProcessingPriority(
  items: TranscriptionQueueItem[],
): TranscriptionQueueItem[] {
  return [...items].sort((a, b) => {
    // Pending items come first
    if (a.status === "pending" && b.status !== "pending") return -1;
    if (b.status === "pending" && a.status !== "pending") return 1;

    // Sort by creation time (oldest first) - ensures chunks process in order
    return a.createdAt - b.createdAt;
  });
}

/**
 * Processes a single queue item.
 * Returns the outcome (success or error with classification).
 */
export async function processItem(
  item: TranscriptionQueueItem,
  blob: Blob,
  processFn: ProcessChunkFn,
): Promise<TranscriptionOutcome> {
  console.log(
    `${LOG_PREFIX()} Processing item: id=${item.id}, noteId=${item.noteId}, retry=${item.retryCount}`,
  );
  try {
    const result = await processFn(item, blob);
    if (result.success) {
      console.log(
        `${LOG_PREFIX()} Item processed successfully: id=${item.id}, transcriptId=${result.transcriptId}`,
      );
    } else {
      console.log(
        `${LOG_PREFIX()} Item processing failed: id=${item.id}, permanent=${result.permanent}, error=${result.error}`,
      );
    }
    return result;
  } catch (error) {
    console.error(
      `${LOG_PREFIX()} Item processing threw error: id=${item.id}`,
      error,
    );
    const classification = classifyError(error);
    return {
      success: false,
      permanent: classification.permanent,
      error: classification.message,
    };
  }
}
